import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Send, AlertCircle, CheckCircle, Loader2, CreditCard, Zap, Calendar, Copy, Check, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { fireConversion } from '../lib/refTracker';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYPAL_BUTTONS = {
  standard: '5U4KVT2YYKHDG',
  priority: 'D4JGWT4TARCCW'
};

// Separate PayPal Button Component to avoid React DOM conflicts
const PayPalButton = ({ buttonId, onLoad }) => {
  const containerRef = useRef(null);
  const buttonRendered = useRef(false);

  useEffect(() => {
    // Reset when buttonId changes
    buttonRendered.current = false;
    if (containerRef.current) {
      // Clear DOM mount point for PayPal SDK remount (safe — no user input involved)
      containerRef.current.replaceChildren();
    }
    
    let checkInterval = null;
    let timeoutId = null;
    
    const renderButton = () => {
      if (window.paypal && window.paypal.HostedButtons && containerRef.current && !buttonRendered.current) {
        buttonRendered.current = true;
        window.paypal.HostedButtons({
          hostedButtonId: buttonId,
        }).render(containerRef.current)
          .then(() => onLoad && onLoad())
          .catch((err) => console.error('PayPal button error:', err));
      }
    };

    // Check if PayPal is already loaded
    if (window.paypal && window.paypal.HostedButtons) {
      renderButton();
    } else {
      // Wait for PayPal SDK to load
      checkInterval = setInterval(() => {
        if (window.paypal && window.paypal.HostedButtons) {
          clearInterval(checkInterval);
          renderButton();
        }
      }, 100);
      
      // Cleanup interval after 10 seconds
      timeoutId = setTimeout(() => {
        if (checkInterval) clearInterval(checkInterval);
      }, 10000);
    }
    
    return () => {
      if (checkInterval) clearInterval(checkInterval);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [buttonId, onLoad]);

  return <div ref={containerRef} id="paypal-button-container" />;
};

const IntakeForm = () => {
  const [formData, setFormData] = useState({
    clientName: '',
    email: '',
    phone: '',
    platform: '',
    accountName: '',
    accountType: '',
    associatedEmail: '',
    associatedPhone: '',
    suspensionDate: '',
    suspensionReason: '',
    additionalInfo: '',
    hasAppealed: '',
    appealOutcome: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [caseId, setCaseId] = useState(null);
  const [copiedCaseId, setCopiedCaseId] = useState(false);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('standard');
  
  const handlePayPalLoad = useCallback(() => {
    setPaypalLoaded(true);
  }, []);
  
  const handlePackageChange = (pkg) => {
    setSelectedPackage(pkg);
    setPaypalLoaded(false);
  };

  const copyCaseIdToClipboard = () => {
    if (caseId) {
      navigator.clipboard.writeText(caseId);
      setCopiedCaseId(true);
      toast.success('Case ID copied!');
      setTimeout(() => setCopiedCaseId(false), 2000);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.clientName || !formData.email || !formData.phone || !formData.platform || !formData.accountName || !formData.accountType || !formData.associatedEmail || !formData.suspensionDate || !formData.suspensionReason || !formData.hasAppealed) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/intake`, formData);
      const submittedCaseId = response.data?.caseId;
      setCaseId(submittedCaseId);
      setSubmitted(true);
      toast.success('Your intake form has been submitted successfully!');
      
      // Track conversion with GA4
      if (typeof window.gtag === 'function') {
        // GA4 event
        window.gtag('event', 'generate_lead', {
          value: 100,
          currency: 'USD',
          form_name: 'intake_form',
          platform: formData.platform
        });
        // Custom intake_submit event
        window.gtag('event', 'intake_submit', {
          event_category: 'lead',
          event_label: 'intake_form',
          platform: formData.platform
        });
      }
      
      // Track conversion with Reddit Pixel
      if (typeof window.rdt === 'function') {
        window.rdt('track', 'Lead', {
          conversionId: submittedCaseId
        });
      }
      
      // Custom tracking
      if (window.sdcTracking && typeof window.sdcTracking.trackFormSubmit === 'function') {
        window.sdcTracking.trackFormSubmit('intake_form', {
          platform: formData.platform,
          account_type: formData.accountType
        });
      }

      // Referral-code conversion tracking. Fires only if the visitor
      // originally arrived with ?ref=CODE persisted by refTracker.
      fireConversion({ caseId: submittedCaseId, tier: "intake" });
    } catch (error) {
      console.error('Error submitting intake form:', error);
      toast.error('Failed to submit form. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="intake" className="bg-[#302f2c] py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-[#3f4816] flex items-center justify-center mb-6">
            <CheckCircle className="w-10 h-10 text-[#d9fb06]" />
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-[#d9fb06] uppercase mb-4">
            Thank You!
          </h2>
          <p className="text-lg text-[#888680] mb-6">
            Your intake form has been received. Our team will review your case and contact you within 24-48 hours.
          </p>
          
          {/* Case ID Display */}
          {caseId && (
            <div className="bg-[#1a1c1b] border-2 border-[#d9fb06]/50 rounded-xl p-6 mb-8">
              <p className="text-sm text-[#888680] uppercase tracking-wide mb-2">Your Case ID</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-2xl md:text-3xl font-mono font-bold text-[#d9fb06]" data-testid="submitted-case-id">
                  {caseId}
                </span>
                <button
                  onClick={copyCaseIdToClipboard}
                  className="p-2 rounded-lg bg-[#3f4816]/50 text-[#888680] hover:text-[#d9fb06] transition-colors"
                  title="Copy Case ID"
                >
                  {copiedCaseId ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-[#888680] mt-3">
                Save this ID to track your case status at any time
              </p>
              <Link 
                to={`/track-case?id=${caseId}`}
                className="inline-flex items-center gap-2 mt-4 text-[#d9fb06] hover:underline text-sm font-medium"
              >
                <Search className="w-4 h-4" />
                Track Your Case
              </Link>
            </div>
          )}
          
          {/* PayPal Payment Section */}
          <div className="bg-[#1a1c1b] border border-[#3f4816]/50 rounded-2xl p-8 mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <CreditCard className="w-6 h-6 text-[#d9fb06]" />
              <h3 className="text-xl font-bold text-[#dfddd6]">
                Ready to Proceed?
              </h3>
            </div>
            <p className="text-[#888680] mb-6">
              Secure your spot and start the dispute process immediately. Payment can also be made after our team contacts you.
            </p>
            
            {/* Package Selection Tabs */}
            <div className="flex justify-center gap-3 mb-6">
              <button
                onClick={() => handlePackageChange('standard')}
                data-testid="select-standard-package"
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold uppercase text-sm transition-all ${
                  selectedPackage === 'standard'
                    ? 'bg-[#d9fb06] text-[#1a1c1b]'
                    : 'bg-[#302f2c] text-[#888680] border border-[#3f4816] hover:border-[#d9fb06]/50'
                }`}
              >
                <CreditCard className="w-4 h-4" />
                Standard $888
              </button>
              <button
                onClick={() => handlePackageChange('priority')}
                data-testid="select-priority-package"
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold uppercase text-sm transition-all ${
                  selectedPackage === 'priority'
                    ? 'bg-[#d9fb06] text-[#1a1c1b]'
                    : 'bg-[#302f2c] text-[#888680] border border-[#3f4816] hover:border-[#d9fb06]/50'
                }`}
              >
                <Zap className="w-4 h-4" />
                Priority $1,333
              </button>
            </div>
            
            {/* Package Description */}
            <p className="text-sm text-[#888680] mb-4">
              {selectedPackage === 'standard' 
                ? 'Standard: Documentation preparation for official appeal submission'
                : 'Priority: Expedited preparation with priority support'
              }
            </p>
            
            {/* PayPal Button Container */}
            <div 
              data-testid="paypal-button-container"
              className="flex flex-col items-center justify-center min-h-[200px] w-full"
              style={{ minWidth: '300px' }}
            >
              {!paypalLoaded && (
                <div className="flex items-center gap-2 text-[#888680]">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Loading payment options...</span>
                </div>
              )}
              <div className="w-full max-w-[400px]">
                <PayPalButton 
                  buttonId={PAYPAL_BUTTONS[selectedPackage]} 
                  onLoad={handlePayPalLoad} 
                />
              </div>
            </div>
            
            <p className="text-xs text-[#888680] mt-4">
              Secure payment powered by PayPal. Venmo also accepted.
            </p>
          </div>
          
          {/* Free Suspension Analyzer prompt — replaces the calendar booking block */}
          <div className="bg-[#1a1c1b] border border-[#3f4816]/50 rounded-2xl p-8 mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Calendar className="w-6 h-6 text-[#d9fb06]" />
              <h3 className="text-xl font-bold text-[#dfddd6]">
                Not ready to commit?
              </h3>
            </div>
            <p className="text-[#888680] mb-6">
              Run our free Suspension Analyzer for a policy-mapped diagnosis in 60 seconds — or give us a call.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/analyze"
                data-testid="intake-analyze-btn"
                className="inline-flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 px-6 py-3 rounded-full font-bold uppercase text-sm transition-all"
              >
                Analyze My Suspension
              </a>
              <a
                href="tel:808-301-2134"
                data-testid="intake-call-btn"
                className="inline-flex items-center gap-2 bg-transparent border-2 border-[#d9fb06] text-[#d9fb06] hover:bg-[#d9fb06] hover:text-[#1a1c1b] px-6 py-3 rounded-full font-bold uppercase text-sm transition-all"
              >
                Call 808-301-2134
              </a>
            </div>
          </div>
          
          <Button
            onClick={() => {
              setSubmitted(false);
              setPaypalLoaded(false);
              setSelectedPackage('standard');
            }}
            variant="outline"
            className="border-[#3f4816] text-[#888680] hover:bg-[#3f4816]/20 hover:text-[#d9fb06] rounded-full px-8 py-6 font-bold uppercase"
            data-testid="submit-another-case-btn"
          >
            Submit Another Case
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section id="intake" className="bg-[#302f2c] py-24 md:py-32">
      <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-[#888680] text-sm font-medium uppercase tracking-widest">
            Start Your Case
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-[#d9fb06] uppercase leading-[0.9] mt-4 mb-6">
            Intake Form
          </h2>
          <p className="text-lg text-[#888680] leading-relaxed">
            Tell us about your suspended account. We'll review your case and respond within 24-48 hours.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} data-track="lead-form" id="intake-form" className="max-w-4xl mx-auto">
          <div className="bg-[#1a1c1b] border border-[#3f4816]/50 rounded-2xl p-8 md:p-12">
            
            {/* Personal Information */}
            <div className="mb-10">
              <h3 className="text-xl font-bold text-[#d9fb06] mb-6 uppercase tracking-wide">
                Personal Information
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="clientName" className="text-[#dfddd6] mb-2 block">
                    Full Name <span className="text-[#d9fb06]">*</span>
                  </Label>
                  <Input
                    id="clientName"
                    name="clientName"
                    value={formData.clientName}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] focus:border-[#d9fb06] focus:ring-[#d9fb06]/20"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[#dfddd6] mb-2 block">
                    Email Address <span className="text-[#d9fb06]">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] focus:border-[#d9fb06] focus:ring-[#d9fb06]/20"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="phone" className="text-[#dfddd6] mb-2 block">
                    Phone Number <span className="text-[#d9fb06]">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] focus:border-[#d9fb06] focus:ring-[#d9fb06]/20"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Account Information */}
            <div className="mb-10">
              <h3 className="text-xl font-bold text-[#d9fb06] mb-6 uppercase tracking-wide">
                Account Information
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="platform" className="text-[#dfddd6] mb-2 block">
                    Social Media Platform <span className="text-[#d9fb06]">*</span>
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange('platform', value)}>
                    <SelectTrigger className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6] focus:ring-[#d9fb06]/20">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#302f2c] border-[#3f4816]">
                      <SelectItem value="Facebook" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">Facebook</SelectItem>
                      <SelectItem value="Instagram" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">Instagram</SelectItem>
                      <SelectItem value="YouTube" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">YouTube</SelectItem>
                      <SelectItem value="TikTok" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">TikTok</SelectItem>
                      <SelectItem value="Twitter/X" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">Twitter/X</SelectItem>
                      <SelectItem value="LinkedIn" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">LinkedIn</SelectItem>
                      <SelectItem value="Uber Eats" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">Uber Eats</SelectItem>
                      <SelectItem value="Reddit" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">Reddit</SelectItem>
                      <SelectItem value="Snapchat" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">Snapchat</SelectItem>
                      <SelectItem value="Multiple" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">Multiple Platforms</SelectItem>
                      <SelectItem value="Other" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="accountType" className="text-[#dfddd6] mb-2 block">
                    Account Type <span className="text-[#d9fb06]">*</span>
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange('accountType', value)}>
                    <SelectTrigger className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6] focus:ring-[#d9fb06]/20">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#302f2c] border-[#3f4816]">
                      <SelectItem value="Personal" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">Personal</SelectItem>
                      <SelectItem value="Business" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">Business</SelectItem>
                      <SelectItem value="Both" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">Both Personal & Business</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="accountName" className="text-[#dfddd6] mb-2 block">
                    Account Name/Handle(s) <span className="text-[#d9fb06]">*</span>
                  </Label>
                  <Input
                    id="accountName"
                    name="accountName"
                    value={formData.accountName}
                    onChange={handleChange}
                    placeholder="@username or Account Name (list all affected accounts)"
                    className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] focus:border-[#d9fb06] focus:ring-[#d9fb06]/20"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="associatedEmail" className="text-[#dfddd6] mb-2 block">
                    Email Associated with Account <span className="text-[#d9fb06]">*</span>
                  </Label>
                  <Input
                    id="associatedEmail"
                    name="associatedEmail"
                    type="email"
                    value={formData.associatedEmail}
                    onChange={handleChange}
                    placeholder="account@email.com"
                    className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] focus:border-[#d9fb06] focus:ring-[#d9fb06]/20"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="associatedPhone" className="text-[#dfddd6] mb-2 block">
                    Phone Associated with Account
                  </Label>
                  <Input
                    id="associatedPhone"
                    name="associatedPhone"
                    type="tel"
                    value={formData.associatedPhone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] focus:border-[#d9fb06] focus:ring-[#d9fb06]/20"
                  />
                </div>
              </div>
            </div>

            {/* Suspension Details */}
            <div className="mb-10">
              <h3 className="text-xl font-bold text-[#d9fb06] mb-6 uppercase tracking-wide">
                Suspension Details
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="suspensionDate" className="text-[#dfddd6] mb-2 block">
                    Date of Suspension <span className="text-[#d9fb06]">*</span>
                  </Label>
                  <Input
                    id="suspensionDate"
                    name="suspensionDate"
                    type="date"
                    value={formData.suspensionDate}
                    onChange={handleChange}
                    className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6] focus:border-[#d9fb06] focus:ring-[#d9fb06]/20"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="hasAppealed" className="text-[#dfddd6] mb-2 block">
                    Have you already appealed? <span className="text-[#d9fb06]">*</span>
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange('hasAppealed', value)}>
                    <SelectTrigger className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6] focus:ring-[#d9fb06]/20">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#302f2c] border-[#3f4816]">
                      <SelectItem value="Yes" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">Yes</SelectItem>
                      <SelectItem value="No" className="text-[#dfddd6] focus:bg-[#3f4816] focus:text-[#d9fb06]">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="suspensionReason" className="text-[#dfddd6] mb-2 block">
                    Reason Given for Suspension <span className="text-[#d9fb06]">*</span>
                  </Label>
                  <Textarea
                    id="suspensionReason"
                    name="suspensionReason"
                    value={formData.suspensionReason}
                    onChange={handleChange}
                    placeholder="Paste any message or notification you received from the platform regarding your suspension..."
                    rows={4}
                    className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] focus:border-[#d9fb06] focus:ring-[#d9fb06]/20 resize-none"
                    required
                  />
                </div>
                {formData.hasAppealed === 'Yes' && (
                  <div className="md:col-span-2">
                    <Label htmlFor="appealOutcome" className="text-[#dfddd6] mb-2 block">
                      Appeal Outcome
                    </Label>
                    <Textarea
                      id="appealOutcome"
                      name="appealOutcome"
                      value={formData.appealOutcome}
                      onChange={handleChange}
                      placeholder="What was the response to your appeal?"
                      rows={3}
                      className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] focus:border-[#d9fb06] focus:ring-[#d9fb06]/20 resize-none"
                    />
                  </div>
                )}
                <div className="md:col-span-2">
                  <Label htmlFor="additionalInfo" className="text-[#dfddd6] mb-2 block">
                    Additional Relevant Information
                  </Label>
                  <Textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    placeholder="Any other details that might help us understand your case better..."
                    rows={4}
                    className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] focus:border-[#d9fb06] focus:ring-[#d9fb06]/20 resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Notice */}
            <div className="flex items-start gap-3 p-4 bg-[#3f4816]/20 rounded-lg border border-[#3f4816]/50 mb-8">
              <AlertCircle className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#888680]">
                By submitting this form, you authorize Social Dispute Co to review your case for documentation preparation services. We do not provide outcome guarantees—platform decisions are outside our control. We will contact you to discuss our services and fees.
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full py-6 text-lg font-bold uppercase tracking-tight transition-transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Submit Intake Form
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default IntakeForm;
